"use client";

import { createContext, useCallback, useContext } from "react";

import { messageId } from "./messageId.ts";
import { DEFAULT_REQUEST_LOCALE, SUPPORTED_REQUEST_LOCALES, type SupportedLocale } from "./requestLocale.ts";
import { formatMessage, type Translate } from "./translate.ts";

/** One locale's translations, keyed by `messageId` of the English source string. */
export type LocaleMessages = Readonly<Record<string, string>>;

export type ShellI18nValue = {
  locale: SupportedLocale;
  /** `locale`, or on web the target of a switch waiting for the server re-render. */
  requestedLocale: SupportedLocale;
  messages: LocaleMessages;
  setLocale: (locale: string) => void;
};

/**
 * The interface language the shell renders in. The host provides it (the app
 * loads catalogs and persists the choice); outside a provider (unit tests,
 * isolated renders) everything is source English.
 */
export const ShellI18nContext = createContext<ShellI18nValue>({
  locale: DEFAULT_REQUEST_LOCALE,
  requestedLocale: DEFAULT_REQUEST_LOCALE,
  messages: {},
  setLocale: () => undefined,
});

export function useLocale(): SupportedLocale {
  return useContext(ShellI18nContext).locale;
}

export function useLocales(): readonly SupportedLocale[] {
  return SUPPORTED_REQUEST_LOCALES;
}

/**
 * The locale the interface is showing or switching to. On web a switch shows
 * only after the server re-render remounts the provider; this reports it from
 * the moment it is asked for, so a listener can act before that remount. On
 * desktop the switch is in place and this equals `useLocale()`: a catalog
 * that fails to load never shows and is never reported.
 */
export function useRequestedLocale(): SupportedLocale {
  return useContext(ShellI18nContext).requestedLocale;
}

/** Switches the interface language; the host also remembers the choice. */
export function useSetLocale(): (locale: string) => void {
  return useContext(ShellI18nContext).setLocale;
}

/** Translates a registered English source string, filling `{name}` placeholders. */
export function useTranslate(): Translate {
  const { messages } = useContext(ShellI18nContext);
  return useCallback(
    (value, options) => formatMessage(messages[messageId(value)] ?? value, options),
    [messages],
  );
}
